import { Link, useParams } from "react-router-dom";
import { ContextBar } from "../components/ContextBar";
import { Figure } from "../components/Figure";
import { ROUTES } from "../lib/routes";
import { initials } from "../lib/staff";
import { useMergedFaculty } from "../lib/mergedFaculty";
import { usePublishedFacultyProfile } from "../lib/publishedFacultyProfile";
import { useScrollPastHero } from "../lib/useScrollPastHero";

export default function FacultyProfile() {
  const { id = "" } = useParams<{ id: string }>();
  const { list: STAFF } = useMergedFaculty();
  const person = STAFF.find((s) => s.slug === id);
  const extras = usePublishedFacultyProfile(person?.slug);
  const [heroRef, scrolledPast] = useScrollPastHero<HTMLElement>();

  if (!person) {
    return (
      <section className="faculty-profile-hero">
        <div className="container">
          <div className="fph-eyebrow">404 · Faculty</div>
          <h1 className="fph-name">Profile not found.</h1>
          <p style={{ color: "rgba(255,255,255,0.7)" }}>
            <Link to={ROUTES.faculty} style={{ color: "var(--gold-400)" }}>Back to the faculty directory →</Link>
          </p>
        </div>
      </section>
    );
  }

  // Sister faculty in the same department for the related rail.
  const sameDept = STAFF.filter((s) => s.deptCode === person.deptCode && s.slug !== person.slug).slice(0, 6);

  // What the page body has to show. Qualifications (in the hero) and the
  // program (in the side card) aren't counted: neither appears in the body.
  const hasPublishedExtras = Boolean(
    extras &&
      (extras.researchAreas.length ||
        extras.memberships.length ||
        extras.coursesTaught.length ||
        extras.publications.length ||
        extras.profileLinks.length),
  );

  return (
    <article>
      <ContextBar
        visible={scrolledPast}
        tag={`${person.deptCode} · ${person.designation.toUpperCase()}`}
        title={person.name}
        back={{ label: "All faculty", to: ROUTES.faculty }}
      />
      <section className="faculty-profile-hero" ref={heroRef}>
        <div className="container">
          <nav className="event-crumbs" aria-label="Breadcrumb">
            <Link to={ROUTES.home}>Home</Link>
            <span aria-hidden>/</span>
            <Link to={ROUTES.faculty}>Faculty</Link>
            <span aria-hidden>/</span>
            <span>{person.deptName}</span>
          </nav>
          <div className="fph-grid">
            <Figure
              media={person.photo}
              fallbackLabel={initials(person.name)}
              aspect="4/5"
              className="fph-avatar"
              sizes="(max-width: 720px) 104px, 200px"
              priority
            />
            <div>
              <div className="fph-eyebrow">{person.deptCode} · {person.teaching ? "Teaching" : "Non-teaching"}</div>
              <h1 className="fph-name">{person.name}</h1>
              <div className="fph-role">
                {person.designation}
                {person.deptName && (
                  <>
                    {" "}
                    <span className="fph-sep" aria-hidden>
                      ·
                    </span>{" "}
                    <span className="fph-dept">{person.deptName}</span>
                  </>
                )}
              </div>
              {extras && (extras.qualifications.length > 0 || extras.researchAreas.length > 0) && (
                <div className="fph-areas">
                  {extras.qualifications.map((q, i) => (
                    <span key={`q${i}`} className="fph-qual">
                      {q}
                    </span>
                  ))}
                  {extras.researchAreas.map((a, i) => <span key={`a${i}`}>{a}</span>)}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="detail-shell">
        <div className="container">
          <div className="detail-shell-grid">
            <div className="detail-body is-wide">
              {!hasPublishedExtras && (
                <p style={{ color: "var(--text-muted)" }}>
                  No further profile details have been published yet.
                </p>
              )}

              {extras && extras.publications.length > 0 && (
                <>
                  <h2>Selected publications</h2>
                  <ul className="faculty-pubs">
                    {extras.publications.map((p, i) => (
                      <li key={i}>
                        {p.year && <span className="py">{p.year}</span>}
                        <span>
                          <span className="pt">{p.title}</span>
                          {p.venue && <div className="pv">{p.venue}</div>}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {extras && extras.coursesTaught.length > 0 && (
                <>
                  <h2>Courses taught</h2>
                  <ul className="detail-list">
                    {extras.coursesTaught.map((c, i) => (
                      <li key={i}>{c.code ? `${c.code} — ${c.title}` : c.title}</li>
                    ))}
                  </ul>
                </>
              )}

              {extras && extras.researchAreas.length > 0 && (
                <>
                  <h2>Research areas</h2>
                  <div className="center-focus center-focus--nude">
                    {extras.researchAreas.map((a, i) => <span key={i}>{a}</span>)}
                  </div>
                </>
              )}

              {extras && extras.memberships.length > 0 && (
                <>
                  <h2>Professional memberships</h2>
                  <div className="center-focus center-focus--nude">
                    {extras.memberships.map((m, i) => <span key={i}>{m}</span>)}
                  </div>
                </>
              )}

              {extras && extras.profileLinks.length > 0 && (
                <>
                  <h2>Profile links</h2>
                  <ul className="detail-list">
                    {extras.profileLinks.map((l, i) => (
                      <li key={i}>
                        <a
                          href={l.url}
                          target="_blank"
                          rel="noreferrer noopener"
                          aria-label={`${l.label || l.url} (external site)`}
                        >
                          {l.label || l.url} <span aria-hidden="true">↗</span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>

            <aside className="detail-meta-card is-accented">
              <div className="dmc-block">
                <div className="dmc-label">Department</div>
                <div className="dmc-value">
                  <Link to={`${ROUTES.department}/${person.deptCode.toLowerCase()}`}>{person.deptName}</Link>
                </div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">Designation</div>
                <div className="dmc-value">{person.designation}</div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">Category</div>
                <div className="dmc-value">{person.teaching ? "Teaching staff" : "Non-teaching staff"}</div>
              </div>
              {extras?.program && (
                <div className="dmc-block">
                  <div className="dmc-label">Programme</div>
                  <div className="dmc-value">{extras.program.name}</div>
                </div>
              )}
            </aside>
          </div>
        </div>
      </section>

      {sameDept.length > 0 && (
        <section className="event-related">
          <div className="container">
            <div className="er-head">
              <div className="kicker">{person.deptCode} · Department</div>
              <h2>Other faculty in {person.deptName}</h2>
            </div>
            <div className="center-faculty" style={{ background: "transparent" }}>
              {sameDept.map((s) => (
                <Link key={s.slug} to={`${ROUTES.faculty}/${s.slug}`} className="fa-card">
                  <Figure
                    media={s.photo}
                    fallbackLabel={initials(s.name)}
                    aspect="1/1"
                    className="fa-photo"
                    sizes="38px"
                  />
                  <div className="fa-info">
                    <span className="fa-name">{s.name}</span>
                    <span className="fa-role">{s.designation}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
